import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { FaFlask, FaTools, FaCog, FaPlug, FaChartLine, FaCheckCircle, FaHandshake, FaWater, FaClipboardCheck, FaCogs, FaBoxOpen } from 'react-icons/fa';

const Services = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={8}>
              <h1 className="display-4 fw-bold mb-4">Our Services</h1>
              <p className="lead mb-4">
                Comprehensive water treatment services designed to ensure efficient, reliable, 
                and cost-effective water system performance across industrial applications.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 1. Industrial Chemical Supply */}
      <section className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">1. Industrial Chemical Supply</h2>
              <p className="lead">
                We supply premium-quality water treatment chemicals formulated to protect systems 
                and improve operational efficiency.
              </p>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col lg={6}>
              <h4 className="mb-3">Applications:</h4>
              <ul>
                <li>Boiler water treatment (scale, corrosion, oxygen control)</li>
                <li>Cooling tower treatment (biofouling, scaling, corrosion)</li>
                <li>RO membrane protection (antiscalants, cleaners)</li>
                <li>Wastewater treatment (coagulants, flocculants, disinfectants)</li>
              </ul>
            </Col>
            <Col lg={6}>
              <h4 className="mb-3">Value to Clients:</h4>
              <ul>
                <li>Reduced equipment downtime</li>
                <li>Extended asset lifespan</li>
                <li>Lower operating costs</li>
                <li>Improved system performance</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 2. Technical Services & Water Management */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">2. Technical Services & Water Management</h2>
              <p className="lead">
                Our technical team provides data-driven solutions to optimize water system performance.
              </p>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col lg={6}>
              <h4 className="mb-3">Services Include:</h4>
              <ul>
                <li>Comprehensive water analysis (raw, treated, boiler, cooling, condensate)</li>
                <li>Chemical dosing program design and optimization</li>
                <li>System audits and performance assessments</li>
                <li>Monthly reporting and KPI tracking</li>
              </ul>
            </Col>
            <Col lg={6}>
              <h4 className="mb-3">Key Benefits:</h4>
              <ul>
                <li>Improved system efficiency</li>
                <li>Compliance with industry standards</li>
                <li>Early detection of operational issues</li>
                <li>Data-driven decision making</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 3. Engineering & Project Solutions */}
      <section className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">3. Engineering & Project Solutions</h2>
              <p className="lead">
                We design, install, and commission water treatment systems tailored to your operational needs.
              </p>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col lg={6}>
              <h4 className="mb-3">Systems Offered:</h4>
              <ul>
                <li>Water softening systems</li>
                <li>Reverse osmosis (RO) plants</li>
                <li>Demineralization (DM) plants</li>
                <li>Effluent treatment plants (ETP)</li>
              </ul>
            </Col>
            <Col lg={6}>
              <h4 className="mb-3">Project Scope:</h4>
              <ul>
                <li>System design and sizing</li>
                <li>Equipment supply and installation</li>
                <li>Commissioning and training</li>
                <li>After-sales technical support</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 4. Equipment & Accessories Supply */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">4. Equipment & Accessories Supply</h2>
              <p className="lead">
                We provide high-quality equipment to support effective water treatment operations.
              </p>
            </Col>
          </Row>
          
          <Row>
            <Col>
              <h4 className="mb-3">Products Include:</h4>
              <Row>
                <Col md={6} lg={3} className="mb-3">
                  <Card className="h-100 text-center">
                    <Card.Body>
                      <div className="feature-icon"><FaWater /></div>
                      <Card.Title>Chemical Dosing Pumps</Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6} lg={3} className="mb-3">
                  <Card className="h-100 text-center">
                    <Card.Body>
                      <div className="feature-icon"><FaClipboardCheck /></div>
                      <Card.Title>Test Kits</Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6} lg={3} className="mb-3">
                  <Card className="h-100 text-center">
                    <Card.Body>
                      <div className="feature-icon"><FaCogs /></div>
                      <Card.Title>Filtration Systems</Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6} lg={3} className="mb-3">
                  <Card className="h-100 text-center">
                    <Card.Body>
                      <div className="feature-icon"><FaBoxOpen /></div>
                      <Card.Title>Industrial Salt & Consumables</Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Our Approach */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">Our Approach</h2>
              <p className="lead">We follow a structured approach to deliver consistent results</p>
            </Col>
          </Row>
          <Row>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaClipboardCheck /></div>
                <h5>Site Assessment & Water Analysis</h5>
                <p className="small">Comprehensive evaluation of your water systems and quality parameters</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaTools /></div>
                <h5>Solution Design & Recommendation</h5>
                <p className="small">Customized treatment programs and system recommendations</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaCog /></div>
                <h5>Implementation & Monitoring</h5>
                <p className="small">Professional installation and continuous performance tracking</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaChartLine /></div>
                <h5>Continuous Optimization</h5>
                <p className="small">Ongoing adjustments and improvements for maximum efficiency</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Service Commitment */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">Service Commitment</h2>
              <p className="lead">At NIKDIP, we are committed to:</p>
            </Col>
          </Row>
          <Row>
            <Col md={4} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaChartLine /></div>
                <h5>Delivering Measurable Results</h5>
                <p className="small">Quantifiable improvements in system performance and efficiency</p>
              </div>
            </Col>
            <Col md={4} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaHandshake /></div>
                <h5>Providing Responsive Technical Support</h5>
                <p className="small">Quick response times and expert assistance when you need it</p>
              </div>
            </Col>
            <Col md={4} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaCheckCircle /></div>
                <h5>Ensuring Long-term Client Satisfaction</h5>
                <p className="small">Building lasting partnerships through consistent quality service</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-primary text-white">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 fw-bold mb-4">Need Expert Support for Your Water Systems?</h2>
              <p className="lead mb-4">
                Contact us today to discuss your requirements or request a detailed technical assessment.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Button variant="warning" size="lg" className="cta-button">
                  Request a Quote
                </Button>
                <Button variant="outline-light" size="lg">
                  Book a Site Audit
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Services;
